import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { authApi, extractApiError } from '@/lib/api'
import { useAuthStore } from '@/stores/auth.store'
import { Button, Input } from '@/components/ui'
import type { UserRole } from '@/types'

export function InvitePage() {
  const { token = '' } = useParams()
  const navigate = useNavigate()
  const setAuth = useAuthStore((s) => s.setAuth)
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [checking, setChecking] = useState(true)
  const [inviteEmail, setInviteEmail] = useState('')

  useEffect(() => {
    let cancelled = false
    if (!token) {
      setError('Ссылка приглашения недействительна')
      setChecking(false)
      return
    }
    authApi
      .checkInvite(token)
      .then((response) => {
        if (cancelled) return
        const body = response.data as { email?: string; status?: string; success?: boolean }
        if (body.status === 'used') {
          setError('Приглашение уже использовано. Войдите по email и паролю.')
          return
        }
        if (body.status === 'expired') {
          setError('Срок действия приглашения истёк. Запросите новую ссылку у оператора.')
          return
        }
        if (body.email) {
          setInviteEmail(body.email)
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(extractApiError(err) || 'Приглашение недействительно или просрочено')
        }
      })
      .finally(() => {
        if (!cancelled) setChecking(false)
      })
    return () => {
      cancelled = true
    }
  }, [token])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (password.length < 8) {
      setError('Пароль не менее 8 символов')
      return
    }
    if (password !== confirm) {
      setError('Пароли не совпадают')
      return
    }
    setLoading(true)
    setError('')
    try {
      const response = await authApi.acceptInvite(token, password)
      const rd = response.data as Record<string, unknown>
      if (rd?.success === false) {
        setError((rd?.error as string) || 'Не удалось активировать приглашение')
        return
      }
      const role = (rd.role as UserRole) || 'admin'
      setAuth(role, (rd.role_name as string) || 'Администратор', {
        userId: rd.user_id as string | undefined,
        email: rd.email as string | undefined,
        tenantId: rd.tenant_id as string | undefined,
        tenantSlug: rd.tenant_slug as string | undefined,
      })
      navigate(role === 'admin' ? '/admin' : '/worker', { replace: true })
    } catch (err) {
      setError(extractApiError(err) || 'Приглашение недействительно или просрочено')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-lg p-8">
        <h1 className="text-xl font-semibold text-gray-800 mb-2 text-center">Активация кабинета</h1>
        <p className="text-sm text-gray-500 mb-6 text-center">
          {inviteEmail ? `Аккаунт: ${inviteEmail}` : 'Задайте пароль для входа в TrueMark'}
        </p>
        {error && (
          <div className="mb-4 rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-700">{error}</div>
        )}
        {checking ? (
          <p className="text-center text-sm text-gray-500">Проверка приглашения…</p>
        ) : error ? null : (
        <form onSubmit={handleSubmit} className="space-y-3">
          <Input
            type="password"
            placeholder="Новый пароль"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="new-password"
            required
          />
          <Input
            type="password"
            placeholder="Повторите пароль"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            autoComplete="new-password"
            required
          />
          <Button type="submit" className="w-full" isLoading={loading}>
            Создать аккаунт
          </Button>
        </form>
        )}
      </div>
    </div>
  )
}
